/**
 * Shell intents — a way for a page to hand work to a shell surface it cannot
 * see.
 *
 * A page and a shell widget are siblings: the widget is mounted in the header,
 * the page under the router outlet, and neither is an ancestor of the other. So
 * a chart that wants to say "ask about this" has no context, no ref and no props
 * reaching the surface that would answer. This is the missing edge, and it is
 * deliberately the *only* one — a narrow, id-keyed hand-off rather than a
 * general event bus.
 *
 * The shell stays feature-agnostic: ids are opaque strings, payloads are
 * `unknown`, and nothing here knows what any of them mean. Features publish
 * typed wrappers over their own id (see `features/chat/intent.ts`), so call
 * sites still get compile-time checking without the shell learning what a
 * feature is (docs/adr/002-modular-feature-registry.md).
 *
 * Availability is reactive on purpose. A deployment with the assistant flag off
 * mounts no handler, `available` is false, and the entry points that would send
 * to it render nothing — rather than offering the user a button that silently
 * does nothing.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

type IntentHandler = (payload: unknown) => void;

interface IntentApi {
  /** Ids that currently have a handler mounted. */
  available: readonly string[];
  /** Register `handler` for `id`; returns the unregister function. */
  register: (id: string, handler: IntentHandler) => () => void;
  /** Deliver `payload` to `id`. No-op when nothing is listening. */
  send: (id: string, payload: unknown) => void;
}

const NO_INTENTS: IntentApi = {
  available: [],
  register: () => () => {},
  send: () => {},
};

const IntentContext = createContext<IntentApi | null>(null);

export function IntentProvider({ children }: { children: ReactNode }) {
  // Handlers live in a ref, their ids in state. Sending must not depend on a
  // render having happened, but *offering* to send must re-render the pages
  // that show the entry points — so the two are stored separately rather than
  // one being derived from the other.
  const handlers = useRef(new Map<string, IntentHandler>());
  const [available, setAvailable] = useState<readonly string[]>([]);

  const register = useCallback((id: string, handler: IntentHandler) => {
    handlers.current.set(id, handler);
    setAvailable((prev) => (prev.includes(id) ? prev : [...prev, id]));
    return () => {
      handlers.current.delete(id);
      setAvailable((prev) => prev.filter((entry) => entry !== id));
    };
  }, []);

  const send = useCallback((id: string, payload: unknown) => {
    handlers.current.get(id)?.(payload);
  }, []);

  const value = useMemo<IntentApi>(
    () => ({ available, register, send }),
    [available, register, send],
  );

  return <IntentContext.Provider value={value}>{children}</IntentContext.Provider>;
}

/** Inert outside a provider, so a surface stays renderable in isolation. */
function useIntents(): IntentApi {
  return useContext(IntentContext) ?? NO_INTENTS;
}

/**
 * Handle intents sent to `id` for as long as this component is mounted.
 *
 * `handler` may be a fresh closure every render — it is read through a ref, so
 * only `id` causes a re-registration. Without that, a handler closing over
 * conversation state would unregister and re-register on every keystroke, and a
 * page that sent in that window would find nothing listening.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hooks colocated with their provider
export function useIntentHandler(id: string, handler: IntentHandler): void {
  const { register } = useIntents();
  const latest = useRef(handler);

  useEffect(() => {
    latest.current = handler;
  });

  useEffect(() => register(id, (payload) => latest.current(payload)), [id, register]);
}

export interface IntentSender {
  /** Whether anything is currently mounted to handle this id. */
  available: boolean;
  send: (payload: unknown) => void;
}

/** Send intents to `id`, and know whether that is worth offering. */
// eslint-disable-next-line react-refresh/only-export-components -- hooks colocated with their provider
export function useIntentSender(id: string): IntentSender {
  const { available, send } = useIntents();
  return useMemo(
    () => ({
      available: available.includes(id),
      send: (payload: unknown) => send(id, payload),
    }),
    [id, available, send],
  );
}
